// Eş portföyü lot listesi (Task 11).
//
// Açık lot'lar (closed_at IS NULL) + opsiyonel kapalılar. Soft delete butonu
// her satırda — confirm() yok, optimistic DELETE; hata olursa parent rollback.

import type { SpouseLotRow } from "../types/api";

interface Props {
  rows: SpouseLotRow[];
  onDelete: (id: number) => void;
  pendingIds: Set<number>;
}

function formatUnits(n: number): string {
  return n.toLocaleString("tr-TR", {
    maximumFractionDigits: 2,
  });
}

function formatNav(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  return n.toLocaleString("tr-TR", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 6,
  });
}

function formatTl(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  return n.toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPct(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  const sign = n >= 0 ? "+" : "−";
  const abs = Math.abs(n * 100).toFixed(2).replace(".", ",");
  return `${sign}%${abs}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function EsLotTablosu({ rows, onDelete, pendingIds }: Props) {
  if (rows.length === 0) {
    return (
      <div className="rounded-xl bg-ink-800/60 border border-dashed border-ink-700 p-6 text-center text-ink-400 text-sm">
        Henüz eş lot kaydı yok. Yukarıdaki formdan ilkini ekle.
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-ink-800 border border-ink-700 overflow-hidden">
      <table className="w-full text-[12px]" aria-label="Eş lot tablosu">
        <thead>
          <tr className="text-ink-400 text-[10px] uppercase tracking-wider bg-ink-900/60">
            <th className="text-left font-medium py-2 px-3">Fon</th>
            <th className="text-left font-medium py-2 px-3">Tarih</th>
            <th className="text-right font-medium py-2 px-3">Adet</th>
            <th className="text-right font-medium py-2 px-3">Maliyet</th>
            <th className="text-right font-medium py-2 px-3">Değer</th>
            <th className="text-right font-medium py-2 px-3">PnL</th>
            <th className="text-right font-medium py-2 px-3" />
          </tr>
        </thead>
        <tbody className="fon-dili">
          {rows.map((r) => {
            const pending = pendingIds.has(r.id);
            const pnlColor =
              r.unrealized_pnl_tl === null
                ? "text-ink-400"
                : r.unrealized_pnl_tl >= 0
                  ? "text-gain-400"
                  : "text-loss-400";
            return (
              <tr
                key={r.id}
                className="border-t border-ink-700/60 hover:bg-ink-700/20"
              >
                <td className="py-2 px-3">
                  <div className="text-ink-100 font-medium">{r.fund_code}</div>
                  {r.fund_name && (
                    <div className="text-[10px] text-ink-400 truncate max-w-[140px]">
                      {r.fund_name}
                    </div>
                  )}
                </td>
                <td className="py-2 px-3 text-ink-300">
                  {formatDate(r.purchase_date)}
                </td>
                <td className="py-2 px-3 text-right text-ink-200">
                  {formatUnits(r.remaining_units)}
                </td>
                <td className="py-2 px-3 text-right text-ink-300">
                  {formatNav(r.cost_per_unit)}
                </td>
                <td className="py-2 px-3 text-right text-ink-200">
                  {formatTl(r.current_value_tl)}
                </td>
                <td className={`py-2 px-3 text-right ${pnlColor}`}>
                  <div>{formatTl(r.unrealized_pnl_tl)}</div>
                  <div className="text-[10px] opacity-70">
                    {formatPct(r.unrealized_pnl_pct)}
                  </div>
                </td>
                <td className="py-2 px-3 text-right">
                  <button
                    type="button"
                    onClick={() => onDelete(r.id)}
                    disabled={pending}
                    aria-label={`Lot #${r.id} sil`}
                    className="text-ink-400 hover:text-loss-400 disabled:opacity-30 disabled:cursor-not-allowed transition px-1"
                  >
                    {pending ? "…" : "×"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
